import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api/client';
import FeeRuleForm, { FEE_TYPE_SHORT, TREATMENTS, dates, emptyRule, formFromRule, payloadFromForm, ruleValue, scopeText } from './fees/FeeRuleForm';

const TREATMENT_BADGE = {
  standard: { label: 'Country fee', bg: '#e0f2fe', color: '#075985' },
  individual: { label: 'Individual fee', bg: '#ede9fe', color: '#5b21b6' },
  exemption: { label: 'Approved exemption (0)', bg: '#fef3c7', color: '#92400e' },
  missing: { label: 'No country fee — bookings paused', bg: '#fee2e2', color: '#991b1b' },
  conflict: { label: 'Conflict', bg: '#fee2e2', color: '#991b1b' },
  none: { label: 'No commission', bg: '#f1f5f9', color: '#475569' },
};
const errText = (e, fallback) => e?.response?.data?.error || fallback;

function Badge({ t }) {
  const b = TREATMENT_BADGE[t] || TREATMENT_BADGE.none;
  return <span style={{ ...s.badge, background: b.bg, color: b.color }}>{b.label}</span>;
}

// Admin → Provider / Customer → Account → Fees & Agreements (Kayd, 26.10 +
// 27.10.2026): the country fee, the account's individual rules and the fee
// that applies today. Three clearly separate settings — use the country fee,
// set an individual fee, or grant an explicit fee exemption (Managing
// Director / Owner only) — plus "Return to Default – Country Fee". Changes
// never touch the country rule or other accounts and apply to new bookings.
export default function AccountFees({ user }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { form, id }
  const [saving, setSaving] = useState(false);
  const [returning, setReturning] = useState(null); // { fee_model, reason }

  const load = useCallback(() => {
    api.get(`/api/fees/admin/accounts/${user.id}/fees`)
      .then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(e.response?.status === 403 ? 'hidden' : errText(e, 'Could not load the fees of this account.')));
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  if (error === 'hidden') return null;
  const canEdit = !!data?.can_edit;
  const canExempt = !!data?.can_exempt;
  const account = data?.account || user;

  const save = async (form, id) => {
    setSaving(true);
    try {
      const payload = payloadFromForm(form, account.id);
      if (id) await api.patch(`/api/fees/admin/agreements/${id}`, payload); else await api.post('/api/fees/admin/agreements', payload);
      toast.success('Saved — applies to new bookings');
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e, 'Could not save')); } finally { setSaving(false); }
  };

  const returnToStandard = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/api/fees/admin/accounts/${account.id}/return-to-standard`, { fee_model: returning.fee_model, reason: returning.reason });
      toast.success(r.data.ended?.length ? `Ended ${r.data.ended.join(', ')} — the country fee applies to new bookings` : 'Nothing to end — the country fee already applies');
      setReturning(null);
      load();
    } catch (err) { toast.error(errText(err, 'Could not return to the country fee')); }
  };

  const endOne = async (a) => {
    const reason = window.prompt(`Return to Default – Country Fee: end ${a.agreement_number}? New bookings then use the country fee. Please enter the reason (saved in the log).`);
    if (!reason || !reason.trim()) return;
    try { await api.post(`/api/fees/admin/agreements/${a.id}/deactivate`, { reason }); toast.success(`${a.agreement_number} ended`); load(); } catch (e) { toast.error(errText(e, 'Could not end the rule')); }
  };

  const active = (data?.agreements || []).filter((a) => a.is_active);
  const ended = (data?.agreements || []).filter((a) => !a.is_active);
  const isProvider = !!data?.is_provider;

  return (
    <section style={s.box} aria-label="Fees and agreements">
      <div style={s.head}>
        <h3 style={s.h3}>Fees &amp; Agreements</h3>
        {canEdit && !editing && (
          <div style={s.btns}>
            <button type="button" style={s.btn} onClick={() => setEditing({ form: emptyRule({ fee_model: 'booking_fee', treatment: 'individual' }) })}>Set individual fee</button>
            {canExempt && <button type="button" style={s.btnWarn} onClick={() => setEditing({ form: emptyRule({ fee_model: 'booking_fee', treatment: 'exemption' }) })}>Grant fee exemption</button>}
            <button type="button" style={s.btnGhost} onClick={() => setReturning({ fee_model: 'booking_fee', reason: '' })}>Return to Default – Country Fee</button>
          </div>
        )}
      </div>
      {data && <FeeSetting agreements={data.agreements || []} />}
      <p style={s.hint}>Changes affect only this account — never the country fee or other accounts — and apply to new bookings only. Confirmed bookings keep their saved price. The country used is the country of the listing or vehicle.{canEdit && !canExempt ? ' Fee exemptions can only be granted by the Managing Director / Owner.' : ''}</p>
      {error && <p style={s.err}>{error}</p>}
      {!data && !error && <p style={s.hint}>Loading…</p>}

      {editing && (
        <FeeRuleForm initial={editing.form} id={editing.id} account={account} isProvider={isProvider} canExempt={canExempt} saving={saving} onSave={save} onCancel={() => setEditing(null)} />
      )}

      {returning && (
        <form style={s.inline} onSubmit={returnToStandard}>
          <strong>Return to Default – Country Fee</strong>
          <span style={s.hint}>Ends the active individual fee{canExempt ? ' / exemption' : ''} of this type for this account. New bookings then use the country fee — never a fee of 0.</span>
          <div style={s.row}>
            <select style={s.input} value={returning.fee_model} onChange={(e) => setReturning({ ...returning, fee_model: e.target.value })} aria-label="Fee type">
              <option value="booking_fee">NULA-MOVE booking fee</option>
              {isProvider && <option value="provider_commission">Provider commission</option>}
            </select>
            <input style={{ ...s.input, flex: '2 1 200px' }} placeholder="Reason (saved in the log) *" value={returning.reason} onChange={(e) => setReturning({ ...returning, reason: e.target.value })} required />
            <button type="submit" style={s.btn}>Return to country fee</button>
            <button type="button" style={s.btnGhost} onClick={() => setReturning(null)}>Cancel</button>
          </div>
        </form>
      )}

      {data && (
        <>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Category</th><th style={s.th}>Country fee</th><th style={s.th}>Booking fee now</th>
                {isProvider && <th style={s.th}>Commission now</th>}
              </tr></thead>
              <tbody>
                {data.treatments.map((t) => (
                  <tr key={`${t.country}-${t.category}`}>
                    <td style={s.td}>{t.category_label}<div style={s.muted}>{t.country || '—'}</div></td>
                    <td style={s.td}>{t.standard ? <>{t.standard.value}<div style={s.muted}>{t.standard.ref}</div></> : <span style={s.warnText}>No country rule</span>}</td>
                    <td style={s.td}>
                      <Badge t={t.booking_fee.treatment} />
                      {t.booking_fee.value && <div>{t.booking_fee.value}</div>}
                      {t.booking_fee.ref && t.booking_fee.treatment !== 'standard' && <div style={s.muted}>{t.booking_fee.ref}</div>}
                      {t.booking_fee.refs && <div style={s.muted}>{t.booking_fee.refs.join(', ')}</div>}
                      {t.listing_rules.filter((r) => r.fee_model === 'booking_fee').map((r) => <div key={r.ref} style={s.muted}>{r.ref}: {r.value} for {r.listings.join(', ')}</div>)}
                    </td>
                    {isProvider && (
                      <td style={s.td}>
                        <Badge t={t.commission?.treatment} />
                        {t.commission?.value && <div>{t.commission.value}</div>}
                        {t.listing_rules.filter((r) => r.fee_model === 'provider_commission').map((r) => <div key={r.ref} style={s.muted}>{r.ref}: {r.value} for {r.listings.join(', ')}</div>)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 style={s.h4}>Individual rules of this account ({active.length} active)</h4>
          {active.length === 0 && <p style={s.hint}>None — the country fee applies.</p>}
          {active.map((a) => (
            <div key={a.id} style={s.rule}>
              <div style={{ minWidth: 0 }}>
                <code>{a.agreement_number}</code> · {FEE_TYPE_SHORT[a.fee_model]} · {TREATMENTS[a.treatment] || a.treatment}
                <div>{ruleValue(a)} · {scopeText(a)}</div>
                <div style={s.muted}>{dates(a.valid_from, a.valid_to)}{a.change_reason ? ` · ${a.change_reason}` : ''}</div>
              </div>
              {canEdit && a.fee_model !== 'customer_subscription' && (a.treatment !== 'exemption' || canExempt) && (
                <div style={s.btns}>
                  <button type="button" style={s.link} onClick={() => setEditing({ id: a.id, form: formFromRule(a) })}>Edit</button>
                  <button type="button" style={{ ...s.link, color: '#b91c1c' }} onClick={() => endOne(a)}>Return to Default – Country Fee</button>
                </div>
              )}
            </div>
          ))}
          {ended.length > 0 && (
            <details style={s.details}>
              <summary style={s.summary}>Ended / inactive rules ({ended.length})</summary>
              {ended.map((a) => <div key={a.id} style={s.small}><code>{a.agreement_number}</code> · {FEE_TYPE_SHORT[a.fee_model]} · {ruleValue(a)} · {scopeText(a)} · {dates(a.valid_from, a.valid_to)}</div>)}
            </details>
          )}
          {data.log.length > 0 && (
            <details style={s.details}>
              <summary style={s.summary}>Change log ({data.log.length})</summary>
              {data.log.map((l) => (
                <div key={l.id} style={s.small}>
                  {new Date(l.created_at).toLocaleString()} · {l.editor_name || 'Staff'} · {l.entity_ref || l.entity_type} {l.action.replace(/_/g, ' ')}
                  {l.reason ? ` — ${l.reason}` : ''}
                  {Object.entries(l.changes || {}).filter(([k]) => ['treatment', 'value_type', 'rate_percent', 'amount_cents', 'currency', 'calc_base', 'scope_type', 'categories', 'valid_from', 'valid_to', 'is_active'].includes(k)).map(([k, c]) => (
                    <div key={k} style={s.muted}>{k.replace(/_/g, ' ')}: {JSON.stringify(c.from)} → {JSON.stringify(c.to)}</div>
                  ))}
                </div>
              ))}
            </details>
          )}
        </>
      )}
    </section>
  );
}

// Which of the three settings applies to the NULA-MOVE booking fee of this
// account today (account-wide view; listing rules are shown per category).
function FeeSetting({ agreements }) {
  const today = new Date().toISOString().slice(0, 10);
  const live = agreements.filter((a) => a.is_active && a.fee_model === 'booking_fee' && (!a.valid_from || a.valid_from <= today) && (!a.valid_to || a.valid_to >= today));
  const current = live.some((a) => a.treatment === 'exemption') ? 'exemption' : live.length ? 'individual' : 'country';
  const opts = [['country', 'Use country fee'], ['individual', 'Individual fee'], ['exemption', 'Explicit fee exemption']];
  return (
    <div style={s.setting} role="group" aria-label="Fee setting of this account">
      <span style={s.settingLabel}>Booking fee setting:</span>
      {opts.map(([k, l]) => <span key={k} style={{ ...s.settingOpt, ...(current === k ? s.settingOn : {}) }}>{current === k ? '● ' : '○ '}{l}</span>)}
    </div>
  );
}

const s = {
  setting: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '8px 0 2px', fontSize: 12.5 },
  settingLabel: { fontWeight: 700, color: '#334155' },
  settingOpt: { padding: '3px 9px', borderRadius: 999, border: '1px solid #e2e8f0', color: '#64748b' },
  settingOn: { borderColor: '#0B6A4A', background: '#ecfdf5', color: '#065f46', fontWeight: 700 },
  box: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, background: '#fff', minWidth: 0 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  h3: { fontSize: 14, fontWeight: 800, margin: 0 },
  h4: { fontSize: 13, fontWeight: 700, margin: '12px 0 6px' },
  hint: { fontSize: 12.5, color: '#64748b', margin: '6px 0' },
  err: { color: '#b91c1c', fontSize: 13 },
  btns: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  btn: { padding: '7px 12px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' },
  btnWarn: { padding: '7px 12px', borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' },
  btnGhost: { padding: '7px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 12.5, cursor: 'pointer' },
  link: { border: 'none', background: 'none', color: '#0B6A4A', fontWeight: 700, cursor: 'pointer', fontSize: 12.5, padding: '2px 4px' },
  inline: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0', fontSize: 13 },
  row: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, flex: '1 1 160px', minWidth: 0, boxSizing: 'border-box' },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginTop: 6 },
  table: { width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 12.5 },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #e5e7eb', color: '#64748b', fontSize: 11.5 },
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, marginBottom: 2 },
  muted: { fontSize: 11.5, color: '#64748b', wordBreak: 'break-word' },
  warnText: { color: '#b91c1c', fontWeight: 600 },
  rule: { display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', padding: '6px 0', fontSize: 13 },
  details: { marginTop: 8, fontSize: 12.5 },
  summary: { cursor: 'pointer', fontWeight: 700 },
  small: { borderTop: '1px solid #f1f5f9', padding: '4px 0', fontSize: 12.5, wordBreak: 'break-word' },
};
